import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  performersSliceSelector,
  sendPerformerEditLinkEmail,
  createPerformerEditLink,
  deletePerformer,
  fetchPerformers,
} from "../../../../../redux/features/performersSlice";
import ConfirmationDialog from "../../../../ConfirmationDialog";
import LoadingOverlay from "../../../../LoadingOverlay";
import PerformerForm from "../PerformerForm/PerformerForm";

const PerformerQRDialog = ({ open, url, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>
      Edit link – scan to open
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
      {url && <QRCodeCanvas value={url} size={200} />}
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Performer can scan this to open their edit page
      </Typography>
    </DialogContent>
  </Dialog>
);

const PerformerFormPage = () => {
  const { organization } = useOutletContext();
  const { performerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const performersState = useSelector((state) => performersSliceSelector(state));
  const { performers = {}, performersFetched } = performersState || {};
  const performer = performerId ? performers[performerId] : null;
  const isEdit = Boolean(performerId && performer);
  const { id: organizationId } = organization || {};

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [snackMessage, setSnackMessage] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  if (!organization || !organizationId) {
    return null;
  }

  const listPath = `/organizations/${organizationId}/performers`;

  if (performerId && performersFetched && !performer) {
    return (
      <Box sx={{ paddingTop: "20px" }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Performer not found
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          This performer may have been removed.
        </Typography>
        <Button variant="contained" onClick={() => navigate(listPath)}>
          Back to performers
        </Button>
      </Box>
    );
  }

  const handleSuccess = () => {
    navigate(listPath);
  };

  const handleCancel = () => {
    navigate(listPath);
  };

  const handleEmailLink = async () => {
    setMenuAnchor(null);
    if (!performer?.id) return;
    setActionLoading(true);
    try {
      await dispatch(
        sendPerformerEditLinkEmail({
          organizationId,
          performerId: performer.id,
        }),
      ).unwrap();
      setSnackMessage("Edit link sent to " + (performer.email || "performer"));
    } catch (err) {
      setSnackMessage(err?.message || "Failed to send email");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyLink = async () => {
    setMenuAnchor(null);
    if (!performer?.id) return;
    setActionLoading(true);
    try {
      const result = await dispatch(
        createPerformerEditLink({
          organizationId,
          performerId: performer.id,
        }),
      ).unwrap();
      const url = result?.editUrl;
      if (url && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setSnackMessage("Edit link copied to clipboard");
      } else {
        setSnackMessage("Edit link ready");
      }
    } catch (err) {
      setSnackMessage(err?.message || "Failed to create link");
    } finally {
      setActionLoading(false);
    }
  };

  const handleShowQR = async () => {
    setMenuAnchor(null);
    if (!performer?.id) return;
    setActionLoading(true);
    try {
      const result = await dispatch(
        createPerformerEditLink({
          organizationId,
          performerId: performer.id,
        }),
      ).unwrap();
      const url = result?.editUrl;
      if (url) {
        setQrUrl(url);
        setQrDialogOpen(true);
      }
    } catch (err) {
      setSnackMessage(err?.message || "Failed to create link");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setMenuAnchor(null);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteConfirmOpen(false);
    if (!performer?.id) return;
    setActionLoading(true);
    try {
      await dispatch(
        deletePerformer({
          organizationId,
          performerId: performer.id,
        }),
      );
      dispatch(fetchPerformers({ organizationId }));
      navigate(listPath);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ paddingTop: "20px" }}>
      <LoadingOverlay show={actionLoading} />
      <ConfirmationDialog
        open={deleteConfirmOpen}
        title="Delete performer"
        message="Are you sure you want to remove this performer from the roster? They will be removed from any event or session assignments."
        handleConfirm={handleDeleteConfirm}
        handleCancel={() => setDeleteConfirmOpen(false)}
        okButtonText="Delete"
      />
      <PerformerQRDialog
        open={qrDialogOpen}
        url={qrUrl}
        onClose={() => {
          setQrDialogOpen(false);
          setQrUrl("");
        }}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h2" flexGrow={1}>
          {isEdit ? "Edit performer" : "Add performer"}
        </Typography>
        {isEdit && (
          <Tooltip title="Actions">
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label="Actions"
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: "45px" }}
      >
        <Tooltip
          title={
            performer?.email?.trim()
              ? ""
              : "Add an email address to this performer to send an edit link."
          }
          placement="left"
        >
          <span style={{ display: "inline-flex", width: "100%" }}>
            <MenuItem
              disabled={!performer?.email?.trim()}
              onClick={handleEmailLink}
            >
              <EmailIcon fontSize="small" sx={{ mr: 1 }} />
              Email link to performer
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem onClick={handleCopyLink}>
          <LinkIcon fontSize="small" sx={{ mr: 1 }} />
          Copy edit link
        </MenuItem>
        <MenuItem onClick={handleShowQR}>
          <QrCode2Icon fontSize="small" sx={{ mr: 1 }} />
          Show QR code
        </MenuItem>
        <MenuItem
          onClick={handleDeleteClick}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
      <PerformerForm
        performer={performer}
        organizationId={organizationId}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        onMessage={setSnackMessage}
      />
      <Snackbar
        open={Boolean(snackMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="info" onClose={() => setSnackMessage(null)}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PerformerFormPage;
